import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores';
import './Admin.css';

interface AIProviderOption {
  id: number;
  name: string;
}

interface AiPromptRow {
  id: number;
  providerId: number;
  providerName: string;
  name: string;
  content: string;
  model?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface FormState {
  providerId: string;
  name: string;
  content: string;
  model: string;
  isActive: boolean;
}

const defaultForm: FormState = {
  providerId: '',
  name: '',
  content: '',
  model: '',
  isActive: true,
};

function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return `${str.slice(0, max)}…`;
}

export function AIPrompts() {
  const { accessToken } = useAuthStore();
  const [prompts, setPrompts] = useState<AiPromptRow[]>([]);
  const [providers, setProviders] = useState<AIProviderOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AiPromptRow | null>(null);
  const [formData, setFormData] = useState<FormState>(defaultForm);
  const [modelOptions, setModelOptions] = useState<{ id: string; label: string }[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState('');

  const fetchProviders = useCallback(async () => {
    const response = await fetch('/api/admin/ai-providers', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) throw new Error('AI provider listesi alınamadı');
    const data = (await response.json()) as { id: number; name: string }[];
    setProviders(data.map((p) => ({ id: p.id, name: p.name })));
  }, [accessToken]);

  const fetchPrompts = useCallback(async () => {
    const response = await fetch('/api/admin/ai-prompts', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) throw new Error('Prompt listesi alınamadı');
    const data = (await response.json()) as AiPromptRow[];
    setPrompts(data);
  }, [accessToken]);

  const loadAll = useCallback(async () => {
    try {
      await Promise.all([fetchProviders(), fetchPrompts()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Yükleme hatası');
    } finally {
      setLoading(false);
    }
  }, [fetchProviders, fetchPrompts]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const fetchProviderModels = useCallback(
    async (providerId: string, signal?: AbortSignal) => {
      const pid = parseInt(providerId, 10);
      if (!Number.isFinite(pid) || pid <= 0) {
        setModelOptions([]);
        setModelsError('');
        setModelsLoading(false);
        return;
      }

      setModelsLoading(true);
      setModelsError('');
      try {
        const r = await fetch(`/api/admin/ai-providers/${pid}/models`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal,
        });
        const data = (await r.json()) as { models?: { id: string; label: string }[]; error?: string };
        if (signal?.aborted) return;
        setModelOptions(Array.isArray(data.models) ? data.models : []);
        setModelsError(typeof data.error === 'string' ? data.error : '');
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        setModelOptions([]);
        setModelsError('Modeller alınamadı');
      } finally {
        if (!signal?.aborted) setModelsLoading(false);
      }
    },
    [accessToken]
  );

  useEffect(() => {
    if (!showModal) return;
    const ac = new AbortController();
    void fetchProviderModels(formData.providerId, ac.signal);
    return () => ac.abort();
  }, [showModal, formData.providerId, fetchProviderModels]);

  const handleOpenModal = (row?: AiPromptRow) => {
    setError('');
    setModelsError('');
    setModelOptions([]);
    if (row) {
      setEditing(row);
      setFormData({
        providerId: String(row.providerId),
        name: row.name,
        content: row.content,
        model: row.model ?? '',
        isActive: row.isActive,
      });
    } else {
      setEditing(null);
      const firstId = providers[0]?.id;
      setFormData({
        ...defaultForm,
        providerId: firstId !== undefined ? String(firstId) : '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setFormData(defaultForm);
    setModelOptions([]);
    setModelsError('');
    setModelsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const providerId = parseInt(formData.providerId, 10);
    if (!Number.isFinite(providerId) || providerId <= 0) {
      setError('Geçerli bir provider seçin');
      return;
    }

    const modelTrim = formData.model.trim();
    const body = {
      providerId,
      name: formData.name.trim(),
      content: formData.content,
      isActive: formData.isActive,
      model: modelTrim.length > 0 ? modelTrim : null,
    };

    setActionLoading(editing?.id ?? -1);
    try {
      const url = editing ? `/api/admin/ai-prompts/${editing.id}` : '/api/admin/ai-prompts';
      const method = editing ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message || 'Kayıt başarısız');
      }

      handleCloseModal();
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kayıt başarısız');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (row: AiPromptRow) => {
    if (!confirm(`"${row.name}" promptunu silmek istediğinize emin misiniz?`)) return;
    setActionLoading(row.id);
    setError('');
    try {
      const response = await fetch(`/api/admin/ai-prompts/${row.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message || 'Silinemedi');
      }
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Silinemedi');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>AI Prompts</h1>
          <p>
            Şablon prompt (provider, model, isim, içerik). Film / oturum verisi çalışma anında kod tarafında eklenir; modeller
            sağlayıcı API’sinden çekilir.
          </p>
        </div>
        <button
          className="admin-btn admin-btn-primary"
          onClick={() => handleOpenModal()}
          disabled={providers.length === 0}
          title={providers.length === 0 ? 'Önce en az bir AI provider ekleyin' : undefined}
        >
          + Yeni Prompt
        </button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>Promptlar ({prompts.length})</h2>
        </div>

        {providers.length === 0 ? (
          <div className="admin-empty-state">
            <p>Prompt eklemek için önce AI Providers sayfasından en az bir sağlayıcı tanımlayın.</p>
          </div>
        ) : prompts.length === 0 ? (
          <div className="admin-empty-state">
            <p>Henüz prompt yok.</p>
            <button className="admin-btn admin-btn-primary" onClick={() => handleOpenModal()}>
              İlk promptu ekle
            </button>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>İsim</th>
                  <th>Model</th>
                  <th>İçerik</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {prompts.map((row) => (
                  <tr key={row.id}>
                    <td>{row.providerName}</td>
                    <td>
                      <span className="provider-name">{row.name}</span>
                    </td>
                    <td>
                      <code className="provider-api-key">{row.model?.trim() ? row.model : '—'}</code>
                    </td>
                    <td>
                      <span className="provider-base-url" title={row.content}>
                        {truncate(row.content, 64)}
                      </span>
                    </td>
                    <td>
                      <span className="provider-type-badge">{row.isActive ? 'Aktif' : 'Pasif'}</span>
                    </td>
                    <td>
                      <div className="admin-action-buttons">
                        <button
                          type="button"
                          className="admin-edit-btn"
                          onClick={() => handleOpenModal(row)}
                          disabled={actionLoading === row.id}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="admin-delete-btn"
                          onClick={() => handleDelete(row)}
                          disabled={actionLoading === row.id}
                          title="Sil"
                        >
                          {actionLoading === row.id ? '...' : '🗑'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editing ? 'Prompt düzenle' : 'Yeni prompt'}</h2>
              <button type="button" className="admin-modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label className="admin-form-label">
                  AI Provider *
                  <select
                    className="admin-form-select"
                    value={formData.providerId}
                    onChange={(e) =>
                      setFormData({ ...formData, providerId: e.target.value, model: '' })
                    }
                    required
                  >
                    <option value="" disabled>
                      Seçin…
                    </option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Model
                  <div className="admin-form-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {modelOptions.length > 0 ? (
                      <select
                        className="admin-form-select"
                        value={
                          modelOptions.some((m) => m.id === formData.model)
                            ? formData.model
                            : formData.model
                              ? '__custom__'
                              : ''
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '__custom__') {
                            setFormData({ ...formData, model: formData.model && !modelOptions.some((m) => m.id === formData.model) ? formData.model : '' });
                            return;
                          }
                          setFormData({ ...formData, model: v });
                        }}
                        disabled={modelsLoading || !formData.providerId}
                      >
                        <option value="">(provider varsayılanı / boş)</option>
                        {modelOptions.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.label && m.label !== m.id ? `${m.label} (${m.id})` : m.id}
                          </option>
                        ))}
                        <option value="__custom__">Özel model ID…</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="admin-form-input"
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        placeholder={modelsLoading ? 'Modeller yükleniyor…' : 'Model ID (opsiyonel)'}
                        autoComplete="off"
                        disabled={modelsLoading}
                      />
                    )}
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary"
                      disabled={!formData.providerId || modelsLoading}
                      title="Provider’dan model listesini yenile"
                      onClick={() => void fetchProviderModels(formData.providerId)}
                    >
                      {modelsLoading ? '…' : 'Yenile'}
                    </button>
                  </div>
                  {modelOptions.length > 0 &&
                    formData.model !== '' &&
                    !modelOptions.some((m) => m.id === formData.model) && (
                      <input
                        type="text"
                        className="admin-form-input"
                        style={{ marginTop: 8 }}
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        placeholder="Özel model ID"
                        autoComplete="off"
                      />
                    )}
                  {modelsLoading && (
                    <small className="admin-form-hint">Sağlayıcı API’sinden modeller alınıyor…</small>
                  )}
                  {!modelsLoading && modelsError && (
                    <small className="admin-form-hint">{modelsError} — model ID’yi elle girebilirsiniz.</small>
                  )}
                  {!modelsLoading && !modelsError && modelOptions.length === 0 && formData.providerId && (
                    <small className="admin-form-hint">
                      Bu provider için liste boş; model ID’yi elle yazın veya boş bırakın.
                    </small>
                  )}
                  {!modelsLoading && !modelsError && modelOptions.length > 0 && (
                    <small className="admin-form-hint">{modelOptions.length} model listelendi.</small>
                  )}
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  İsim *
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Örn: intent_first_message"
                    maxLength={128}
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  İçerik *
                  <textarea
                    className="admin-form-textarea"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows={12}
                    placeholder="Prompt metni…"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-setting-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>Aktif</span>
                </label>
              </div>

              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={handleCloseModal}
                  disabled={actionLoading !== null}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={actionLoading !== null}
                >
                  {actionLoading !== null ? 'Kaydediliyor…' : editing ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
